<template>
  <div>
    <h5 v-if="!inlineMode" class="border-bottom pb-1 gold">
      <span v-text="data.title"></span>: <small v-text="data.sub_title"></small>
    </h5>

    <div v-if="data && data.video_link" class="mt-3">
      <b-embed
        :src="videoEmbedUrl"
        allowfullscreen
        aspect="16by9"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      ></b-embed>
    </div>

    <div v-if="data.media">
      <!--
			TODO: Check that data.media can actually be more than one.
			If it is, then remove the v-for or set data.media[0] with checks
			-->
      <div v-for="(media, k) in data.media" :key="'media_' + k">
        <div class="d-flex flex-wrap align-items-center mt-3" >
          <audio
            v-if="media && media.trim() !== '' && media !== 'null'"
            id="'audio_tag_' + k"
            @canplay="updatePaused"
            @playing="updatePaused"
            @play="play"
            @pause="updatePaused"
            @ended="finished"
            @timeupdate="updatePlayTime"
            controls
            controlsList="nodownload"
          >
            <source :src="media" />
          </audio>
          <template
            v-if="
              isMeditationType &&
                audio_sound &&
                paused &&
                duration > audio_sound.duration - currentTime
            "
          >
            <b-button size="sm" variant="outline-success" @click="saveProgress" class="ml-2">
              <i class="fas fa-cloud-upload-alt"></i>
              Save Progress
            </b-button>
          </template>
        </div>
      </div>
      <div v-if="is_saved" class="text-success">Meditation Saved!</div>
    </div>

    <TextClamp v-if="main_text" :html="main_text" class="p-3" />

    <div v-if="!inlineMode" class="border-top text-right">
      <b-button class="mt-3" @click="hideModal()">Cancel</b-button>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import NoSleep from "nosleep.js";
import DOMPurify from "isomorphic-dompurify";
import TextClamp from './programs/TextClamp.vue';

var noSleep = new NoSleep();

export default {
  components: { TextClamp },
  name: "CourseMeditation",
  props: {
    data: {
      type: Object,
      default: null,
    },
    modal_id: {
      type: String,
      default: null,
    },
    inlineMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      audio_sound: null,
      currentTime: 0,
      duration: 0,
      paused: null,
      is_saved: false,
      videoClickReported: false,
      noSleepEnabled: false,
    };
  },
  mounted() {
    // noSleep will be enabled when audio plays
    // Track section open if in inline mode
    if (this.inlineMode && this.data && this.data.challenge_id) {
      this.trackSectionOpen();
    }
  },
  methods: {
    trackSectionOpen() {
      // Lessons (subsections) have a section_id — skip section-level tracking for them
      if (this.data.section_id) return;

      let _this = this;
      setTimeout(() => {
        axios({
          method: "post",
          url: `programs/sections/start/${_this.data.challenge_id}/${_this.data.pkid}`,
          data: {
            token: _this.$store.state.session_id
          }
        })
          .then(function() {
            _this.$emit('section-opened', _this.data.pkid);
          })
          .catch(function(e) {
            console.log("Error: trackSectionOpen:" + e);
          });
      }, 1000);
    },
    hideModal() {
      if (this.noSleepEnabled) {
        noSleep.disable();
        this.noSleepEnabled = false;
      }
      this.audio_sound = null;
      this.$bvModal.hide(this.modal_id);
    },
    updatePlayTime() {
      if (this.paused) {
        // on drag of the player position, set new duration to be
        // time left on player
        this.duration =
          this.audio_sound.duration - this.audio_sound.currentTime;
      }
      this.currentTime = this.audio_sound.currentTime;
    },
    uploadMeditationTime() {
      console.log("submit meditation");
      let params = new URLSearchParams();
      params.append("date", "now");
      params.append("totalTime", this.duration - this.currentTime);
      params.append("default_min", Math.floor(this.duration / 60));
      var now = new Date();
      var TZ_offset = now.getTimezoneOffset();
      params.append("timezone_offset", TZ_offset);

      return axios({
        url: "meditationAdd",
        params: params,
      });
    },
    saveProgress() {
      this.currentTime = this.audio_sound.currentTime;
      let _this = this;
      if (!this.isMeditationType) return;
      this.uploadMeditationTime()
        .then(function() {
          _this.duration = _this.audio_sound.duration - _this.currentTime;
          _this.is_saved = true;
          setTimeout(() => {
            _this.is_saved = false;
          }, 1000);
        })
        .catch(function(e) {
          console.log(e);
        });
    },

    finished() {
      this.currentTime = this.audio_sound.currentTime;
      noSleep.disable();
      this.noSleepEnabled = false;

      if (!this.isMeditationType) {
        this.audio_sound = null;
        return;
      }

      let _this = this;
      _this.uploadMeditationTime()
        .then(function(response) {
          console.log(response.data);
          _this.audio_sound = null;
        })
        .catch(function(e) {
          console.log("Error: finished:" + e);
        });
    },

    updatePaused(event) {
      this.audio_sound = event.target;
      this.paused = event.target.paused;
    },
    play(event) {
      if (event) {
        // Enable noSleep when audio starts playing
        noSleep.enable();
        this.noSleepEnabled = true;

        // on play set audio_sound to the audio target
        this.audio_sound = event.target;

        // if duration is zero, on start, then set it to be the
        // total audio duration
        if (this.duration === 0) this.duration = this.audio_sound.duration;
      }
    },
  },
  computed: {
    playing() {
      return !this.paused;
    },
    isMeditationType() {
      return this.data && this.data.type === 'CourseMeditation';
    },
    videoEmbedUrl() {
      const url = this.data?.video_link;
      if (!url) return '';
      const match = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
      return match ? `https://www.youtube.com/embed/${match[1]}?rel=0` : url;
    },
    main_text() {
      if (!this.data || !this.data.main_text) return '';
      return DOMPurify.sanitize(this.data.main_text, {ADD_ATTR: ['target', 'rel']})
    },
  },
  watch: {},
};
</script>